import equal from 'fast-deep-equal';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { CitationViewer, EvidenceData } from '@/components/citations';
import {
  useCreateIncidentMutation,
  useGetIncidentsQuery,
  useUpdateIncidentMutation,
} from '@/services/api/incidentService';
import { IncidentMentionSchema } from '@/services/types/incident-types';
import { IncidentNote } from '@/services/types/matter-notes-types';

import { MatterNoteConfig } from '../types';
import { DisplayMatterNote, EditMatterNote } from './BaseMatterNoteForm';
import { FormActions } from './components/FormActions';

export interface IncidentNoteFormProps {
  config: MatterNoteConfig;
  matterId: string;
  note?: IncidentNote | null;
  onSaved?: (note: IncidentNote) => void;
  onCancelled?: () => void;
  onDeleted?: () => void;
}

export const IncidentNoteForm: React.FC<IncidentNoteFormProps> = ({
  config,
  matterId,
  note,
  onSaved,
  onCancelled,
  onDeleted,
}) => {
  const isNew = !note?.id;
  const initialValues = note || { matter: matterId };
  const [formValues, setFormValues] =
    useState<Record<string, any>>(initialValues);
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Incident API mutations
  const [createIncident, { isLoading: isCreating }] =
    useCreateIncidentMutation();
  const [updateIncident, { isLoading: isUpdating }] =
    useUpdateIncidentMutation();

  // Fetch incident data with mentions from the new API
  // Only fetch if we have a note (indicating the incident exists)
  const {
    data: incidents,
    isLoading: isIncidentLoading,
    error: incidentError,
  } = useGetIncidentsQuery({ matterId }, { skip: !note });

  // Get the incident data (should be a single item since it's 1-to-1)
  const incidentWithMentions = incidents?.[0];
  const hasEvidence =
    incidentWithMentions?.mentions && incidentWithMentions.mentions.length > 0;

  // Convert incident mentions to evidence data format
  const evidenceData: EvidenceData[] =
    incidentWithMentions?.mentions?.map((mention: IncidentMentionSchema) => ({
      artifact_id: mention.artifact_id,
      citation_ids: mention.citation_ids,
      confidence: mention.confidence,
      context: mention.context,
      created_at: mention.created_at,
      artifact_type: mention.artifact_type,
      rationale: mention.reasoning || null,
    })) || [];

  // Reset form when note changes
  useEffect(() => {
    if (note) {
      setFormValues(note);
      setIsEditing(false);
    }
  }, [note]);

  // Track if form has changes compared to original note
  useEffect(() => {
    if (!isNew) {
      setHasChanges(!equal(note, formValues));
    }
  }, [formValues, note, isNew]);

  const handleFieldChange = (name: string, value: any) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setFormValues(note || { matter: matterId });
    setIsEditing(false);
    setHasChanges(false);
  };

  const handleSave = async () => {
    try {
      let savedNote;

      if (isNew) {
        // For creating a new incident, we need to structure the data according to CreateIncidentSchema
        const incidentData = {
          matter_id: matterId,
          // Map form values to incident schema fields
          date_of_loss: formValues.date_of_loss || null,
          time_of_loss: formValues.time_of_loss || null,
          location: formValues.location || null,
          weather_conditions: formValues.weather_conditions || null,
          injuries_described: formValues.injuries_described || null,
          incident_type: formValues.incident_type || null,
          hospital: formValues.hospital || null,
          ambulance: formValues.ambulance || null,
          police_report: formValues.police_report || null,
          current_treatment: formValues.current_treatment || null,
          witnesses: formValues.witnesses || null,
          statements: formValues.statements || null,
          photos: formValues.photos || null,
          representation: formValues.representation || null,
          // Include description field as well
          description: formValues.description || null,
        };

        const createdIncident = await createIncident({
          matterId,
          incident: incidentData,
        }).unwrap();

        // Convert the incident response back to the expected note format
        savedNote = {
          id: createdIncident.id,
          matter: matterId,
          description: formValues.description || null,
          created_at: createdIncident.created_at,
          updated_at: createdIncident.modified_at,
          date_of_loss: createdIncident.date_of_loss || null,
          time_of_loss: createdIncident.time_of_loss || null,
          location: createdIncident.location || null,
          weather_conditions: createdIncident.weather_conditions || null,
          injuries_described: createdIncident.injuries_described || null,
          incident_type: createdIncident.incident_type || null,
          hospital: createdIncident.hospital || null,
          ambulance: createdIncident.ambulance || null,
          police_report: createdIncident.police_report || null,
          current_treatment: createdIncident.current_treatment || null,
          witnesses: createdIncident.witnesses || null,
          statements: createdIncident.statements || null,
          photos: createdIncident.photos || null,
          representation: createdIncident.representation || null,
        };

        toast.success(`${config.title} created successfully`);
      } else if (hasChanges && note?.id) {
        // For updating, structure data according to UpdateIncidentSchema
        const incidentData = {
          id: note.id,
          date_of_loss: formValues.date_of_loss || null,
          time_of_loss: formValues.time_of_loss || null,
          location: formValues.location || null,
          weather_conditions: formValues.weather_conditions || null,
          injuries_described: formValues.injuries_described || null,
          incident_type: formValues.incident_type || null,
          hospital: formValues.hospital || null,
          ambulance: formValues.ambulance || null,
          police_report: formValues.police_report || null,
          current_treatment: formValues.current_treatment || null,
          witnesses: formValues.witnesses || null,
          statements: formValues.statements || null,
          photos: formValues.photos || null,
          representation: formValues.representation || null,
          description: formValues.description || null,
        };

        const updatedIncident = await updateIncident({
          matterId,
          incidentId: note.id,
          incident: incidentData,
        }).unwrap();

        // Convert the incident response back to the expected note format
        savedNote = {
          id: updatedIncident.id,
          matter: matterId,
          description: formValues.description || null,
          created_at: updatedIncident.created_at,
          updated_at: updatedIncident.modified_at,
          date_of_loss: updatedIncident.date_of_loss || null,
          time_of_loss: updatedIncident.time_of_loss || null,
          location: updatedIncident.location || null,
          weather_conditions: updatedIncident.weather_conditions || null,
          injuries_described: updatedIncident.injuries_described || null,
          incident_type: updatedIncident.incident_type || null,
          hospital: updatedIncident.hospital || null,
          ambulance: updatedIncident.ambulance || null,
          police_report: updatedIncident.police_report || null,
          current_treatment: updatedIncident.current_treatment || null,
          witnesses: updatedIncident.witnesses || null,
          statements: updatedIncident.statements || null,
          photos: updatedIncident.photos || null,
          representation: updatedIncident.representation || null,
        };

        toast.success(`${config.title} updated successfully`);
      } else {
        savedNote = note;
      }

      setIsEditing(false);
      setHasChanges(false);
      if (savedNote) {
        onSaved?.(savedNote);
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? `Error: ${error.message}`
          : `Failed to save ${config.title.toLowerCase()}`,
      );
    }
  };

  /**
   * Renders the evidence button for extra actions
   */
  const renderEvidenceButton = (noteId?: string) => {
    if (!noteId || !hasEvidence) return null;

    return (
      <CitationViewer
        evidence={evidenceData}
        matterId={matterId}
        dialogTitle="Incident Evidence Sources"
        isLoading={isIncidentLoading}
        error={!!incidentError}
        errorMessage="Unable to load evidence sources"
        buttonClassName="text-xs text-slate-600 hover:text-slate-900"
      />
    );
  };

  // Prepare extra actions content
  const extraActionsContent = renderEvidenceButton(note?.id);

  return (
    <div>
      <div className="rounded-lg border shadow-sm overflow-hidden group">
        <div
          className={`transition-all duration-200 ${
            !isEditing && !isNew
              ? 'opacity-70 group-hover:opacity-100'
              : 'opacity-100'
          }`}
        >
          <FormActions
            isEditing={isEditing}
            isNew={isNew}
            cardinality={config.cardinality}
            onEdit={handleEdit}
            onSave={handleSave}
            onCancel={handleCancel}
            onDelete={undefined} // No delete functionality as requested
            isSaving={isCreating || isUpdating}
            isDeleting={false}
            title={config.title}
            extraContent={extraActionsContent}
          />
        </div>
        <div className="p-3 sm:p-4 pt-1 sm:pt-0">
          <div>
            {isEditing ? (
              <EditMatterNote
                fields={config.fields}
                values={formValues}
                noteId={note?.id || ''}
                onFieldChange={handleFieldChange}
              />
            ) : (
              <DisplayMatterNote fields={config.fields} values={formValues} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
